import { Typography, Card, Stack, Box, Avatar, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import dayjs from 'dayjs';
import { GolfCourse as GolfIcon } from '@mui/icons-material';
import PlayerStatPill from './PlayerStatPill';
import { useWaglRating } from '../hooks/useWaglRating';
import { useAuth } from '../contexts/AuthContext';
import GameMatchResult from './GameMatchResult';

// Custom Competitive icon component
const CompetitiveIcon = ({ sx = {} }) => (
  <svg
    width="14"
    height="10"
    viewBox="0 0 14 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ fontSize: 16, ...sx }}
  >
    <path
      d="M14 0.734419L12.259 6.9644C11.7223 8.83099 10.6968 9.75823 8.87227 9.75823C8.00169 9.75823 7.24516 9.52043 6.58927 9.1163C5.90956 9.52045 5.06813 9.75823 4.10225 9.75823C1.68144 9.75823 0 8.21275 0 5.78738C0 4.50325 0.488912 3.14793 1.46681 1.60243L2.20612 0.425293L3.42247 1.12684L2.73088 2.19687C1.87224 3.49272 1.38333 4.64604 1.38333 5.73975C1.38333 7.39236 2.36115 8.53371 4.11413 8.53371C4.63888 8.53371 5.11058 8.43857 5.51601 8.2721C4.40698 7.11891 3.84657 6.2451 3.84657 5.0086C3.84657 3.16582 4.89589 1.93607 6.51773 1.93607C8.15145 1.93607 9.27243 3.1884 9.27243 5.03118C9.27243 6.23201 8.75962 7.13075 7.61482 8.30777C7.96066 8.45055 8.3237 8.53371 8.74107 8.53371C9.76661 8.53371 10.4583 7.98679 10.8756 6.5125L12.4855 0.734419H14H14ZM6.52961 3.1369C5.70681 3.1369 5.22989 3.86724 5.22989 5.00858C5.22989 6.12612 5.67105 6.71462 6.55351 7.58251C7.48368 6.69094 7.90106 6.07737 7.90106 5.03116C7.90106 3.87797 7.34053 3.13689 6.52961 3.13689V3.1369Z"
      fill="#9B66FF"
    />
  </svg>
);

// Custom Match Play icon component
const MatchPlayIcon = ({ sx = {} }) => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ fontSize: 16, ...sx }}
  >
    <path
      opacity="0.87"
      d="M15.8943 12.9809C15.7813 12.5729 15.5244 12.2511 15.1515 12.0505C14.7627 11.8413 14.3479 11.8061 13.9518 11.9485L11.2694 12.9132C10.9487 13.0286 10.6875 13.234 10.5106 13.4941L8.53627 9.94359L13.6262 0.790517C13.7521 0.564321 13.6707 0.278921 13.4445 0.153188C13.2184 0.0273336 12.9329 0.108754 12.807 0.334951L8.00002 8.97924L3.19289 0.334951C3.06716 0.108754 2.78176 0.0273336 2.55556 0.153188C2.32937 0.278921 2.24794 0.564321 2.37368 0.790517L7.46376 9.94359L5.4894 13.4941C5.31252 13.234 5.05129 13.0286 4.73061 12.9132L2.04824 11.9485C1.65212 11.8061 1.23732 11.8414 0.84853 12.0505C0.475606 12.2511 0.21877 12.5729 0.105733 12.9809C-0.0844529 13.6686 -0.018535 14.3811 0.307271 15.1594C0.547505 15.7331 1.08315 16.0896 1.70522 16.0896H4.25844C4.73537 16.0896 5.16018 15.8631 5.4319 15.5126C5.43508 15.5085 5.43837 15.5045 5.44143 15.5005C5.51894 15.3982 5.58352 15.2855 5.63271 15.1649L8.00002 10.9079L10.3673 15.1649C10.4165 15.2854 10.481 15.3981 10.5586 15.5004C10.5618 15.5046 10.565 15.5087 10.5683 15.5127C10.84 15.8633 11.2648 16.0896 11.7416 16.0896H14.2948C14.9169 16.0896 15.4525 15.7331 15.6926 15.1594C16.0186 14.3811 16.0845 13.6686 15.8943 12.9809Z"
      fill="#216BFF"
    />
  </svg>
);

// Custom Stroke Play icon component
const StrokePlayIcon = ({ sx = {} }) => (
  <svg
    width="12"
    height="17"
    viewBox="0 0 12 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ fontSize: 16, ...sx }}
  >
    <mask
      id="mask0_2981_7323"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="12"
      height="17"
    >
      <path d="M12 0.486084H0V16.4861H12V0.486084Z" fill="white" />
    </mask>
    <g mask="url(#mask0_2981_7323)">
      <path
        d="M5.65525 3.72772C5.65525 2.93073 4.99933 2.28369 4.1914 2.28369C3.38347 2.28369 2.72754 2.93073 2.72754 3.72772C2.72754 4.5247 3.38347 5.17174 4.1914 5.17174C4.99933 5.17174 5.65525 4.5247 5.65525 3.72772Z"
        fill="#06B67B"
      />
      <path
        d="M6.1462 2.06539C6.1462 2.06539 7.27962 3.01394 6.33125 4.08311C5.38288 5.15228 4.48903 5.92807 4.46095 6.43495C4.43451 6.94345 5.13835 7.23682 5.76124 7.71762C6.38412 8.19842 7.57701 9.96352 6.79056 12.1279C6.00411 14.294 3.75545 14.6134 3.75545 14.6134L4.48738 16.4845C4.48738 16.4845 6.73604 15.7901 7.98346 13.9175C9.23088 12.0464 8.8773 9.90811 8.8773 9.90811C8.8773 9.90811 9.79924 11.1908 9.98924 13.1694C10.1792 15.148 10.1247 15.6288 10.1247 15.6288H11.995C11.995 15.6288 12.1305 11.5656 10.7757 9.16002C9.42088 6.75439 7.27962 5.92481 7.27962 5.92481C7.27962 5.92481 8.68895 4.26728 8.52703 2.87704C8.36512 1.4868 7.06483 0.486084 7.06483 0.486084C7.06483 0.486084 0.166853 7.17 0.0660682 7.2792C-0.0363688 7.39003 -0.00497685 7.60027 0.0660682 7.68991C0.137113 7.77955 0.41138 8.08107 0.563383 8.21146C0.715387 8.34184 1.17305 8.18212 1.07226 7.78118C0.971479 7.38025 0.717039 7.2792 0.981393 7.03961C1.24575 6.7984 6.14785 2.06539 6.14785 2.06539"
        fill="#06B67B"
      />
    </g>
  </svg>
);

// TODO: Refactor this component to be less monolithic and reduce Firebase coupling for better unit/component testing
// Consider breaking into:
// 1. GameCard (presentational component) - handles layout and styling
// 2. GameData hook (useGameData) - handles Firebase operations and data fetching
// 3. GamePlayerList component - handles player display logic and avatar rendering
// 4. GameDateDisplay component - handles date formatting and poll display
// 5. GameActions component - handles click handlers and navigation
// This would enable proper unit testing of business logic and component rendering separately
// Player display constants
export const PLAYER_DISPLAY_CONFIG = {
  MAX_VISIBLE_PLAYERS: 4,
  SLICE_START_INDEX: 1,
  SLICE_END_INDEX: 4,
  PLAYER_LIMIT_THRESHOLD_HIGH: 3,
  PLAYER_LIMIT_THRESHOLD_MID: 2,
  PLAYER_LIMIT_FALLBACK: 1,
};

export const getPlayerLimit = (invitations) => {
  if (!invitations) {
    return PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_FALLBACK;
  }
  if (invitations.length > PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_THRESHOLD_HIGH) {
    return PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_FALLBACK;
  }
  if (invitations.length > PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_THRESHOLD_MID) {
    return PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_THRESHOLD_HIGH;
  }
  return invitations.length;
};

const GameV2 = ({
  data,
  isOrganiser = false,
  isPending = false,
  isPast = false,
  hideCTA = false,
}) => {
  console.log('data', data);
  const { db } = useFirebase();
  const navigate = useNavigate();
  const [_invitees] = useState([...(data.invitations || [])]);
  const { currentUser } = useAuth();
  
  // Auto-determine if current user is organiser (override prop if needed)
  const actualIsOrganiser = data.createdBy === currentUser.uid ? true : isOrganiser;

  const [inviteesWithPhotoURL, setInviteesWithPhotoURL] = useState([]);
  const [creatorData, setCreatorData] = useState(null);

  const matchIamAPlayerIn = data.matches?.find((match) =>
    match.players?.find(
      (player) =>
        player.user === currentUser.uid ||
        player.userId === currentUser.uid ||
        player.uid === currentUser.uid
    )
  );

  useEffect(() => {
    const fetchUserData = async () => {
      const _inviteesWithOrganiser = [{ user: data.createdBy }, ..._invitees];
      const invitees = await Promise.all(
        _inviteesWithOrganiser.map(async (invitee) => {
          const userId = invitee?.user || invitee?.uid;
          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists ? userDoc.data() : {};
          return {
            ...invitee,
            uid: userId, // Ensure uid field is set for matching
            user: userId, // Keep user field for backward compatibility
            photoURL: userData?.profileImage || userData?.photoURL || null,
            profileImage: userData?.profileImage || userData?.photoURL || null,
            userName:
              userData?.userName || userData?.username || userData?.displayName || 'Unknown',
            ...userData,
          };
        })
      );
      setInviteesWithPhotoURL(invitees);
      setCreatorData(invitees[0]);
    };
    fetchUserData();
  }, [_invitees, data.createdBy, data.players, db]);

  const gameDateObject = new Date((data.date || new Date()).seconds * 1000);

  const gameFullDateString = () => {
    const dateTimeOptions = data?.dateTimeOptions;
    const firstDateTimeOption = dateTimeOptions?.[0]?.option;
    const moreThanOneOption = dateTimeOptions?.length > 1;
    const optionsString = moreThanOneOption ? `(+${dateTimeOptions?.length})` : '';
    if (!data.date) {
      return `${dayjs(firstDateTimeOption?.toDate()).format(
        'ddd D MMM [at] HH:mm'
      )} ${optionsString}`;
    }
    return dayjs(gameDateObject).format('ddd D MMM [at] HH:mm');
  };

  const handleClickGame = () => {
    navigate(`/games/${data.id}`);
  };

  // Calculate player counts - preserve original data from acceptedBy objects
  let confirmedPlayers = data.dateTimeOptions
    ? Array.from(
        new Map(
          data.dateTimeOptions
            .flatMap((option) => option?.acceptedBy || [])
            .map((item) => [item.uid, item]) // Keep the full original object with uid, userName, acceptedAt
        ).values()
      )
    : [];
  if (confirmedPlayers?.length > 0 && inviteesWithPhotoURL?.length > 0) {
    confirmedPlayers = confirmedPlayers?.map((player) => {
      const _player = inviteesWithPhotoURL?.find((p) => p.uid === player.uid);
      return {
        ..._player,
      };
    });
  }

  // Get unique UIDs from invitations
  const invitedPlayerUids = data.invitations
    ? [...new Set(data.invitations.map((invitation) => invitation.user))]
    : [];

  // Get unique UIDs from confirmed players
  const confirmedPlayerUids = confirmedPlayers.map((player) => player.uid);

  // Calculate unanswered players (invited but not confirmed)
  const unansweredPlayers = invitedPlayerUids.filter(
    (uid) => !confirmedPlayerUids.includes(uid)
  ).length;
  const unavailablePlayers = data.declinedBy ? data.declinedBy.length : 0;

  const iAmInvited =
    data.invitations?.some((invitation) => invitation.user === currentUser.uid) || false;

  const userHasDeclined = (user) => {
    if (!user) return false;
    const userId = user.uid;
    const declinedBy = data.declinedBy || [];
    return declinedBy.includes(userId);
  };

  const hasUserAcceptedADate = (user) => {
    if (!user) return false;
    const userId = user.uid;
    const allAccepted = data?.dateTimeOptions?.flatMap((dt) => dt.acceptedBy);
    const allAcceptedUids = allAccepted.map((accepted) => accepted.uid);
    const hasAccepted = allAcceptedUids.includes(userId);
    return hasAccepted;
  };
  const hasPoll = data.dateTimeOptions?.length > 1;
  const notRespondedYet = !userHasDeclined(currentUser) && !hasUserAcceptedADate(currentUser);
  const hasAccepted = hasUserAcceptedADate(currentUser);
  const iAmInOneOfTheMatches = data.matches?.some((match) =>
    match.players?.some((player) => player.user === currentUser.uid)
  );

  // Helper function to check if match has scores entered
  const hasScores = (match) => {
    if (!match) return false;
    
    // Check for match play results
    if (match.format === 'match' && match.result) {
      return true;
    }
    
    // Check for stroke play scores
    if (match.format === 'stroke' && match.players?.some(player => player.score !== undefined && player.score !== null)) {
      return true;
    }
    
    return false;
  };

  const matchHasScores = hasScores(matchIamAPlayerIn);

  let CTAButtonName = 'View Game';
  let CTAInverted = false;

  const Divider = () => {
    return <Box sx={{ mx: -4, width: 330, height: '1px', backgroundColor: '#E5E7EB', mb: 2 }} />;
  };

  const Subtitle = () => {
    // Scenario 11: Score pending for past games (non-organiser)
    if (isPast && !actualIsOrganiser && !matchHasScores) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ color: '#D2691E', fontSize: '10px' }}>⏳</Typography>
          <Typography sx={{ color: '#D2691E', fontSize: '10px', fontWeight: 600 }}>
            Score pending
          </Typography>
        </Stack>
      );
    }

    // Scenario 10: Past game organiser view without scores - no subtitle needed
    if (isPast && actualIsOrganiser && !matchHasScores) {
      return null;
    }

    if (actualIsOrganiser) {
      // Only show CONFIRMED if status is confirmed AND matches are set
      const hasMatches = data.matches && data.matches.length > 0;
      if (data.status !== 'confirmed' || !hasMatches) {
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ color: '#D2691E', fontSize: '10px' }}>⏳</Typography>
            <Typography sx={{ color: '#D2691E', fontSize: '10px', fontWeight: 600 }}>
              Pending
            </Typography>
          </Stack>
        );
      } else {
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ color: 'green', fontSize: '10px' }}>✅</Typography>
            <Typography sx={{ color: 'green', fontSize: '10px', fontWeight: 600 }}>
              CONFIRMED
            </Typography>
          </Stack>
        );
      }
    }

    if (isPending) {
      if (iAmInvited && notRespondedYet) {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ color: 'purple', fontSize: '10px', fontWeight: 600 }}>
              {hasPoll ? "You've been invited to a poll" : 'Invited you to play'}
            </Typography>
          </Stack>
        );
      }
      if (iAmInOneOfTheMatches) {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ color: 'green', fontSize: '10px', fontWeight: 600 }}>
              I'm In!
            </Typography>
          </Stack>
        );
      }
      if (hasAccepted) {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ color: 'orange', fontSize: '10px', fontWeight: 600 }}>
              Pending
            </Typography>
          </Stack>
        );
      }

      if (hasPoll) {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ color: '#D2691E', fontSize: '10px' }}>⏳</Typography>
            <Typography sx={{ color: '#D2691E', fontSize: '10px', fontWeight: 600 }}>
              Invited you to play
            </Typography>
          </Stack>
        );
      }
    }
  };

  // Scenario 5: Hide CTA if user has responded but game not finalised
  let showCTA = true;
  if (!actualIsOrganiser && isPending && hasAccepted && data.status !== 'confirmed') {
    showCTA = false;
  }

  if (actualIsOrganiser) {
    CTAButtonName = 'View Game';
    CTAInverted = false; // View game is white (non-inverted)
    // Show "Finalise Game" if:
    // - Date not confirmed yet OR
    // - Date confirmed but matches not created yet
    const hasMatches = data.matches && data.matches.length > 0;
    if (data.status !== 'confirmed' || !hasMatches) {
      CTAButtonName = 'Finalise Game';
      CTAInverted = true; // Finalise game is purple (inverted)
    }
  }

  if (!actualIsOrganiser && isPending) {
    if (iAmInvited && notRespondedYet) {
      CTAInverted = true;
      CTAButtonName = 'Respond';
    }
  }

  // Scenario 10: Enter score CTA is now handled by GameMatchResult component
  let playersConfirmed = inviteesWithPhotoURL?.length;
  if (isPast && matchIamAPlayerIn?.players?.length > 0) {
    playersConfirmed = matchIamAPlayerIn.players?.length;
  }

  return (
    <Card
      sx={{
        width: { xs: '100%', sm: '326px' },
        maxWidth: '100%',
        borderRadius: '16px',
        boxShadow: 'none',
        cursor: 'pointer',
      }}
      onClick={handleClickGame}
    >
      <Stack spacing={0}>
        {/* Header with Game Creator Info */}
        <Box sx={{ pb: isPast ? 0 : 1.5, backgroundColor: '#fff' }}>
          {/* Show header only for non-past games */}
          {!isPast && (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                src={creatorData?.photoURL || creatorData?.profileImage}
                alt={creatorData?.userName || creatorData?.username || 'Unknown'}
                sx={{ width: 48, height: 48, bgcolor: '#E9FFEA' }}
              >
                {!creatorData?.photoURL && (
                  <Typography
                    variant="factorA"
                    component="small"
                    sx={{
                      fontSize: 24,
                      lineHeight: 1,
                      color: '#4C4C4C',
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {creatorData?.userName
                      ? creatorData.userName.charAt(0).toUpperCase()
                      : creatorData?.username
                      ? creatorData.username.charAt(0).toUpperCase()
                      : 'W'}
                  </Typography>
                )}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '16px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {creatorData?.userName || creatorData?.username || 'Unknown'}
                </Typography>
                <Subtitle />
              </Box>
              {showCTA && (
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: CTAInverted ? '#8B5CF6' : 'white',
                    // borderRadius: '12/px',
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: '#7C3AED',
                    },
                    color: CTAInverted ? 'white' : 'black',
                  }}
                >
                  {CTAButtonName}
                </Button>
              )}
            </Stack>
          )}
        </Box>

        {!actualIsOrganiser && !isPast && <Divider />}

        {/* Game Details */}
        <Box sx={{ px: 0, pb: 1.5 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              color: '#000000',
              mb: 1,
            }}
          >
            {isPending && hasPoll && !hasAccepted ? 'Dates shown in Poll' : gameFullDateString()}
            {iAmInvited && hasAccepted && !actualIsOrganiser && !isPast && (
              <Box
                component="img"
                src="/confirmed-icon.svg"
                alt="Confirmed"
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            )}
          </Typography>

          {!isPast && (
            <Box>
              <Typography sx={{ fontSize: '12px', color: '#6B7280', mb: 1 }}>
                {!actualIsOrganiser && (
                  <>
                    Players:{' '}
                    <span style={{ color: 'black', fontWeight: 600, paddingRight: '10px' }}>
                      {playersConfirmed}
                    </span>
                  </>
                )}
                Fee:{' '}
                <span style={{ color: 'black', fontWeight: 600, paddingRight: '4px' }}>
                  £{data.cost || 25}PP
                </span>
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={1} sx={{ width: '100%', mb: isPast ? 1 : 0 }}>
            <Chip
              icon={
                data.competitive ? (
                  <CompetitiveIcon sx={{ fontSize: 16 }} />
                ) : (
                  <GolfIcon sx={{ fontSize: 16 }} />
                )
              }
              label={data.competitive ? 'Competitive' : 'Friendly'}
              sx={{
                backgroundColor: data.competitive ? '#F3E8FF' : '#ECFDF5',
                color: data.competitive ? '#7C3AED' : '#059669',
                fontWeight: 600,
                borderRadius: '5px',
                flex: 1,
                '& .MuiChip-icon': {
                  color: data.competitive ? '#7C3AED' : '#059669',
                },
              }}
            />
            {isPast && (
              <Chip
                icon={
                  matchIamAPlayerIn?.format === 'match' ? (
                    <MatchPlayIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <StrokePlayIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={`${matchIamAPlayerIn?.format === 'match' ? 'Match Play' : 'Stroke Play'}`}
                sx={{
                  backgroundColor: matchIamAPlayerIn?.format === 'match' ? '#1F73CC1A' : '#ECFDF5',
                  color: matchIamAPlayerIn?.format === 'match' ? '#216BFF' : '#059669',
                  fontWeight: 600,
                  borderRadius: '5px',
                  flex: 1,
                  '& .MuiChip-icon': {
                    color: matchIamAPlayerIn?.format === 'match' ? '#216BFF' : '#059669',
                  },
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Divider */}

        {/* Players Section - Only for organiser view of non-past games */}
        {actualIsOrganiser && !isPast && (
          <Box sx={{ px: 0, pb: 1.5 }}>
            <Divider />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                color: 'primary',
                mb: 1,
              }}
            >
              Players
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Box
                component="img"
                src="/confirmed-icon.svg"
                alt="Confirmed"
                sx={{ width: 16, height: 16 }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                {confirmedPlayers.length} Accepted
              </Typography>
            </Stack>

            {/* Player List */}
            <Stack direction="row" spacing={-1} sx={{ mb: 2, overflowX: 'auto' }}>
              {confirmedPlayers.slice(0, 6).map((player, index) => {
                const PlayerWithRating = () => {
                  const playerId = player?.uid;
                  const playerName = player.userName || player.username || 'N/A';
                  const { rating: ratingData } = useWaglRating(playerId);

                  return (
                    <Box key={index} sx={{ textAlign: 'center', minWidth: 60 }}>
                      <Avatar
                        src={player.photoURL || player.profileImage}
                        alt={playerName}
                        sx={{
                          width: 48,
                          height: 48,
                          mx: 'auto',
                          mb: 0.5,
                          border: '2px solid #fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          bgcolor: '#E9FFEA',
                        }}
                      >
                        {!player.photoURL && (
                          <Typography
                            variant="factorA"
                            component="small"
                            sx={{
                              fontSize: 24,
                              lineHeight: 1,
                              color: '#4C4C4C',
                              padding: 0,
                              margin: 0,
                            }}
                          >
                            {playerName ? playerName.charAt(0).toUpperCase() : 'W'}
                          </Typography>
                        )}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#374151',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 60,
                        }}
                      >
                        {playerName}
                      </Typography>
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                        <PlayerStatPill
                          waglScore={ratingData?.currentRating || 50}
                          handicap={player?.handicap || 0}
                        />
                      </Box>
                    </Box>
                  );
                };

                return <PlayerWithRating key={player?.uid || index} />;
              })}
            </Stack>

            {/* Player Status Summary */}
            <Stack direction="row" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  component="img"
                  src="/unanswered-icon.svg"
                  alt="Unanswered"
                  sx={{ width: 16, height: 16 }}
                />
                <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 600 }}>
                  {unansweredPlayers} Unanswered
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  component="img"
                  src="/unavailable-icon.svg"
                  alt="Unavailable"
                  sx={{ width: 16, height: 16 }}
                />
                <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 600 }}>
                  {unavailablePlayers} Unavailable
                </Typography>
              </Stack>
            </Stack>
          </Box>
        )}

        {isPending && <div>{/* do nothing for now */}</div>}

        {isPast && data.matches && data.matches.length > 0 && (
          <Box sx={{ px: 0, pb: 1.5 }}>
            <Box sx={{ mb: 2 }}>
              {/* <Typography variant="body2" sx={{ px: 2, color: '#6B7280', mb: 1 }}>
                Match Results
              </Typography> */}
              <Divider />
            </Box>
            <GameMatchResult
              match={matchIamAPlayerIn}
              users={inviteesWithPhotoURL}
              gameId={data.id}
              gameData={data}
              hideCTA={hideCTA}
              isOrganiser={actualIsOrganiser}
            />
          </Box>
        )}

        {/* Spacer for past games without matches (stroke play) to maintain consistent card height */}
        {isPast && matchIamAPlayerIn?.format === 'stroke' && !data.matches && (
          <Box sx={{ px: 0, pb: 1.5 }}>
            <Typography
              variant="body2"
              sx={{ color: 'transparent', fontSize: '14px', fontWeight: 600 }}
            >
              Result 0&0
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mx: -4,
            width: 330,
            height: '1px',
            backgroundColor: '#E5E7EB',
            mb: 1,
            mt: 1,
          }}
        />
        {/* Golf Club Info */}
        <Box sx={{ px: 0, pb: 1.5, pt: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {/* <Avatar alt={data.clubName} sx={{ width: 40, height: 40 }} /> */}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                {data.clubName || ''}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                {data.clubAddress || ''}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

export default GameV2;
